import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shadcn/lib/utils';
import {
  KeyboardShortcutOptions,
  useKeyboardShortcut
} from '@/hooks/useKeyboardShortcut';
import { useRef } from 'react';

const buttonVariants = cva(
  'h-full inline-flex select-none cursor-pointer items-center uppercase justify-center gap-2 whitespace-nowrap rounded-sm text-[11px] leading-1 font-bold tracking-wider ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive',
        ghostDestructive:
          'hover:bg-destructive hover:text-destructive-foreground text-destructive',
        ghostDestructiveSubtle:
          'hover:bg-destructive hover:text-destructive-foreground font-normal',
        outline:
          'border border-input text-accent-foreground/80 hover:bg-accent hover:text-accent-foreground',
        outlinePrimary:
          'border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        ghostSubtle:
          'bg-muted text-muted-foreground hover:brightness-90 border border-border',
        link: 'text-primary underline-offset-4 hover:underline normal-case',
        pushtext:
          'text-primary hover:bg-accent data-[active=true]:text-muted-foreground data-[active=true]:pointer-events-none',
        pushtextMuted:
          'text-muted-foreground hover:bg-accent data-[active=true]:text-muted-foreground data-[active=true]:pointer-events-none'
      },
      size: {
        default: 'h-7 px-4 py-2 ',
        sm: 'h-6 rounded-md px-2 py-1',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-8 w-8 shrink-0',
        smallIcon: 'h-6 w-6 [&_svg]:size-3.5',
        largeIcon: 'size-8 [&_svg]:size-5'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  } & (
    | {
        variant: Extract<
          VariantProps<typeof buttonVariants>['variant'],
          'pushtext'
        >;
        active: boolean;
      }
    | {
        variant?: Exclude<
          VariantProps<typeof buttonVariants>['variant'],
          'pushtext'
        >;
        active?: boolean;
      }
  );

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, active, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        data-active={active}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

const SmartButton = ({
  className,
  variant,
  size,
  asChild = false,
  active,
  shortcut,
  ...props
}: ButtonProps & { shortcut: KeyboardShortcutOptions }) => {
  const Comp = asChild ? Slot : 'button';
  const buttonRef = useRef<HTMLButtonElement>(null);

  useKeyboardShortcut(() => buttonRef.current?.click(), shortcut);

  return (
    <Comp
      data-active={active}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={buttonRef}
      {...props}
    />
  );
};
SmartButton.displayName = 'SmartButton';
export { Button, SmartButton, buttonVariants };
